<template>
  <div class="article-wrapper">
    <div class="reading-progress" :style="{ width: readingProgress + '%' }"></div>

    <nav class="navbar navbar-scrolled">
      <div class="logo" @click="$emit('go-home')">LAIQIU<span>来秋</span></div>
      <div class="nav-links">
        <a href="#content">正文</a>
        <a href="#comments">讨论区</a>
        <a href="#related">灵感延伸</a>
      </div>
      <button class="back-btn" @click="$emit('go-back')">← 返回探索</button>
    </nav>

    <header class="article-hero">
      <div class="hero-bg" :style="{ backgroundImage: `url(${article.cover})` }"></div>
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="article-meta">
          <span class="category">{{ article.category }}</span>
          <span class="dot">·</span>
          <span class="time">{{ article.date }}</span>
          <span class="dot">·</span>
          <span class="read-time">阅读约 {{ article.readTime }} 分钟</span>
        </div>
        <h1>{{ article.title }}</h1>
        <p class="excerpt">{{ article.excerpt }}</p>
      </div>
    </header>

    <main class="article-container">
      <article class="content-body" id="content">

        <div class="ai-summary-box">
          <div class="ai-header">
            <span class="ai-icon">✧</span> AI 提炼脉络
          </div>
          <p>{{ article.aiSummary }}</p>
        </div>

        <section v-html="article.contentHtml" class="markdown-render"></section>

        <div class="article-tags">
          <span v-for="tag in article.tags" :key="tag" class="tag"># {{ tag }}</span>
        </div>

        <footer class="article-footer">
          <div class="interactions">
            <button class="action-btn active"><span class="icon">♥</span> 1.2k 共鸣</button>
            <button class="action-btn"><span class="icon">⚑</span> 收藏模型</button>
            <button class="action-btn"><span class="icon">⚓</span> 分享全息链接</button>
          </div>
        </footer>

        <section id="comments" class="comments-section">
          <h3>Terminal 讨论区 <span class="comment-count">/ 42 节点</span></h3>
          <div class="comment-input-area">
            <div class="avatar-small"></div>
            <textarea placeholder="输入你的思考节点... (支持 Markdown)"></textarea>
            <button class="submit-btn">部署评论</button>
          </div>

          <div class="comment-list">
            <div class="comment-item" v-for="(comment, index) in comments" :key="index">
              <div class="comment-avatar" :style="{ backgroundImage: `url(${comment.avatar})` }"></div>
              <div class="comment-content">
                <div class="comment-meta">
                  <span class="c-author">{{ comment.author }}</span>
                  <span class="c-time">{{ comment.time }}</span>
                </div>
                <p>{{ comment.text }}</p>
                <div class="c-actions">
                  <span>▲ {{ comment.upvotes }}</span>
                  <span>回复</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </article>

      <aside class="article-sidebar">
        <div class="glass-card author-card">
          <div class="avatar" :style="{ backgroundImage: `url(${article.author.avatar})` }"></div>
          <div class="author-info">
            <h4>{{ article.author.name }}</h4>
            <p>{{ article.author.title }}</p>
          </div>
          <div class="author-stats">
            <div><span>89</span>文章</div>
            <div><span>12k</span>关注</div>
          </div>
          <button class="follow-btn">建立连接</button>
        </div>

        <div class="glass-card toc-card">
          <h4>数据目录</h4>
          <ul>
            <li v-for="(item, index) in article.toc" :key="index">
              <a :href="'#' + item.id">{{ item.text }}</a>
            </li>
          </ul>
        </div>
      </aside>
    </main>

    <section id="related" class="related-section">
      <div class="related-container">
        <h3>灵感延伸 / Related Logs</h3>
        <div class="related-grid">
          <div class="related-card" v-for="(item, index) in relatedArticles" :key="index">
            <div class="r-cover" :style="{ backgroundImage: `url(${item.cover})` }"></div>
            <div class="r-info">
              <span class="r-category">{{ item.category }}</span>
              <h4>{{ item.title }}</h4>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="simple-footer">
      <p>© 2026 LAIQIU COMMUNITY · 科技以人文的方式着陆</p>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const readingProgress = ref(0)

const article = {
  title: '在算法的边缘：寻找 2026 年的数字人文主义',
  excerpt: '当生成式 AI 填满了互联网的每一个角落，我们该如何重新定义人类创作的独特性？',
  aiSummary: '本文探讨了在 AI 高度普及的 2026 年，开发者与创作者面临的“同质化”困境。作者提出“代码温度”的概念，呼吁通过跨学科协作与去中心化的确权技术，重新找回数字创作中的人文火花与不可替代性。',
  category: '深度评论',
  date: '2026.04.12',
  readTime: '12',
  cover: 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?q=80&w=2000',
  tags: ['Artificial Intelligence', 'Humanism', 'Web3', 'Philosophy'],
  author: {
    name: 'Archie.Dev',
    title: '来秋首席布道师 / 算法工程师',
    avatar: 'https://i.pravatar.cc/150?u=archie'
  },
  toc: [
    { id: 'sec1', text: '01. 数字洪流中的寂静' },
    { id: 'sec2', text: '02. 注入灵魂的代码' },
    { id: 'sec3', text: '03. 重构社区连接' }
  ],
  contentHtml: `
    <h2 id="sec1">01. 数字洪流中的寂静</h2>
    <p>在这个信息过载的时代，我们每天被数以亿计的 Token 包围。算法比我们更了解自己的喜好，但这种了解往往是基于过去数据的重复，而非对未来灵感的洞察。当机器可以在一秒内生成完美的商业级代码时，作为人类工程师，我们的价值在哪里？</p>
    <blockquote>“算法提供的是正确答案，但只有人类能提出伟大的问题。” —— 这不仅是我们的口号，更是对抗数字虚无的武器。</blockquote>
    
    <h2 id="sec2">02. 注入灵魂的代码</h2>
    <p>看看下面这段被高度优化的 <code>Neuro-Rendering</code> 核心逻辑：</p>
    <pre><code>// 2026 标准量子渲染管道
function injectHumanity(renderContext, emotionData) {
  const baseNoise = QuantumMath.generatePerlin(context.seed);
  // 注入微小的、不可预测的瑕疵，模拟人类的手工质感
  const artifactLayer = applyImperfectFilter(baseNoise, emotionData.variance);
  
  return renderContext.blend(artifactLayer, { mode: 'soft-light' });
}</code></pre>
    <p>当你写下一行能够解决他人问题的代码时，那一刻你不仅是在逻辑世界中堆砌，你是在现实世界中构建连接。这段代码的意义不在于执行效率，而在于它刻意保留了“瑕疵”。这就是我们所说的“代码的温度”。</p>
    <img src="https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=1200" alt="Tech Dashboard" />
    
    <h2 id="sec3">03. 重构社区连接</h2>
    <p>来秋社区存在的意义，就是打破这些由算法加固的“信息茧房”。在这里，我们倡导跨学科的碰撞。我们不需要更多的自动化脚本，我们需要的是懂艺术的程序员，和懂算法的诗人。</p>
  `
}

const comments = [
  { author: 'Cyber_Nomad', time: '2小时前', text: '完全同意“保留瑕疵”的观点。现在很多 AI 生成的 UI 看起来太完美了，反而让人感到一种恐怖谷效应。', avatar: 'https://i.pravatar.cc/150?u=a1', upvotes: 34 },
  { author: 'Neo.Vue', time: '5小时前', text: '代码块里那个 QuantumMath 库是虚构的还是 26 年的新标准？如果是真的，求指路 Github。', avatar: 'https://i.pravatar.cc/150?u=a2', upvotes: 12 }
]

const relatedArticles = [
  { title: 'Web3 与创作者经济：下一次价值迁徙', category: '行业趋势', cover: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=600' },
  { title: '用 Three.js 构建你的精神自留地', category: '技术教程', cover: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=600' },
  { title: '极简主义在复杂系统中的应用法则', category: '设计美学', cover: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600' }
]

const updateScroll = () => {
  const pixels = window.scrollY
  const docHeight = document.body.offsetHeight
  const winHeight = window.innerHeight
  readingProgress.value = (pixels / (docHeight - winHeight)) * 100
}

onMounted(() => {
  window.addEventListener('scroll', updateScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateScroll)
})
</script>

<style scoped>
.article-wrapper {
  background: #050505;
  color: #fff;
  min-height: 100vh;
  font-family: 'Inter', 'PingFang SC', sans-serif;
}

/* 进度条 */
.reading-progress {
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #6d5dfc, #00d2ff);
  z-index: 2000;
  transition: width 0.1s ease;
  box-shadow: 0 0 10px rgba(109, 93, 252, 0.5);
}

/* 导航 */
.navbar {
  position: fixed;
  top: 0;
  width: 100%;
  padding: 1rem 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  background: rgba(5, 5, 5, 0.85);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.logo {
  font-size: 1.5rem;
  font-weight: 800;
  cursor: pointer;
}

.logo span {
  font-weight: 300;
  margin-left: 8px;
  font-size: 1rem;
  opacity: 0.8;
}

.nav-links a {
  color: #fff;
  text-decoration: none;
  margin: 0 1.5rem;
  font-size: 0.9rem;
  opacity: 0.6;
  transition: 0.3s;
}

.nav-links a:hover {
  opacity: 1;
  color: #6d5dfc;
}

.back-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: 0.3s;
}

.back-btn:hover {
  background: #fff;
  color: #000;
}

/* Hero Section */
.article-hero {
  position: relative;
  height: 75vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: brightness(0.4) saturate(1.2);
  transform: scale(1.02);
}

.hero-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background: linear-gradient(to top, #050505 10%, transparent);
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 900px;
  padding: 0 20px;
}

.article-meta {
  font-size: 0.85rem;
  color: #6d5dfc;
  margin-bottom: 25px;
  letter-spacing: 3px;
  text-transform: uppercase;
}

.dot {
  margin: 0 10px;
  opacity: 0.3;
}

.article-hero h1 {
  font-size: 4rem;
  line-height: 1.1;
  margin-bottom: 25px;
  font-weight: 900;
}

.excerpt {
  font-size: 1.25rem;
  opacity: 0.7;
  font-weight: 300;
  line-height: 1.6;
}

/* 主容器布局 */
.article-container {
  max-width: 1200px;
  margin: -60px auto 0;
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 80px;
  position: relative;
  z-index: 10;
  padding: 0 20px 100px;
}

/* AI 摘要框 */
.ai-summary-box {
  background: rgba(109, 93, 252, 0.05);
  border: 1px solid rgba(109, 93, 252, 0.2);
  border-radius: 12px;
  padding: 25px 30px;
  margin-bottom: 50px;
  position: relative;
  overflow: hidden;
}

.ai-summary-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: #6d5dfc;
  box-shadow: 0 0 15px #6d5dfc;
}

.ai-header {
  font-size: 0.9rem;
  color: #6d5dfc;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-icon {
  animation: pulse 2s infinite;
}

.ai-summary-box p {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  margin: 0;
}

@keyframes pulse {
  0% {
    opacity: 0.5;
  }

  50% {
    opacity: 1;
    text-shadow: 0 0 8px #6d5dfc;
  }

  100% {
    opacity: 0.5;
  }
}

/* 正文 Markdown 渲染样式升级 */
.markdown-render {
  font-size: 1.15rem;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 300;
}

.markdown-render :deep(h2) {
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  color: #fff;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.markdown-render :deep(p) {
  margin-bottom: 1.8rem;
}

.markdown-render :deep(blockquote) {
  border-left: 2px solid #6d5dfc;
  padding: 1.5rem 2.5rem;
  background: linear-gradient(90deg, rgba(109, 93, 252, 0.1) 0%, transparent 100%);
  font-style: italic;
  font-size: 1.25rem;
  margin: 3rem 0;
  color: #fff;
}

.markdown-render :deep(pre) {
  background: #0d0d0d;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 12px;
  margin: 2.5rem 0;
  overflow-x: auto;
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.5);
}

.markdown-render :deep(code) {
  font-family: 'Fira Code', 'Courier New', monospace;
  color: #a29bfe;
  font-size: 0.95rem;
}

.markdown-render :deep(p code) {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  color: #fff;
}

.markdown-render :deep(img) {
  width: 100%;
  border-radius: 16px;
  margin: 3rem 0;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* 底部互动区 */
.article-footer {
  margin-top: 60px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.interactions {
  display: flex;
  gap: 15px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 12px 24px;
  border-radius: 30px;
  cursor: pointer;
  transition: 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.action-btn.active {
  background: #6d5dfc;
  border-color: #6d5dfc;
}

/* 评论区 */
.comments-section {
  margin-top: 80px;
}

.comments-section h3 {
  font-size: 1.5rem;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.comment-count {
  font-size: 0.9rem;
  color: #6d5dfc;
  font-weight: normal;
  font-family: monospace;
}

.comment-input-area {
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  align-items: flex-start;
}

.avatar-small {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: #333;
  flex-shrink: 0;
}

.comment-input-area textarea {
  flex: 1;
  height: 100px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  color: #fff;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: 0.3s;
}

.comment-input-area textarea:focus {
  border-color: #6d5dfc;
  background: rgba(109, 93, 252, 0.05);
}

.submit-btn {
  background: #fff;
  color: #000;
  border: none;
  padding: 12px 25px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  align-self: flex-end;
}

.comment-item {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
}

.comment-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-size: cover;
  flex-shrink: 0;
}

.comment-content {
  flex: 1;
}

.comment-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.c-author {
  font-weight: bold;
  font-size: 0.95rem;
}

.c-time {
  font-size: 0.8rem;
  opacity: 0.4;
}

.comment-content p {
  font-size: 0.95rem;
  line-height: 1.6;
  opacity: 0.8;
  margin-bottom: 15px;
}

.c-actions {
  display: flex;
  gap: 20px;
  font-size: 0.85rem;
  opacity: 0.6;
  cursor: pointer;
}

.c-actions span:hover {
  color: #6d5dfc;
  opacity: 1;
}

/* 侧边栏卡片优化 */
.article-sidebar {
  position: relative;
}

.glass-card {
  background: rgba(20, 20, 20, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  padding: 40px 30px;
  border-radius: 20px;
  margin-bottom: 30px;
  position: sticky;
  top: 120px;
}

.author-card {
  text-align: center;
}

.avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  margin: 0 auto 20px;
  background-size: cover;
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.author-info h4 {
  font-size: 1.3rem;
  margin-bottom: 8px;
}

.author-info p {
  font-size: 0.85rem;
  color: #6d5dfc;
  margin-bottom: 25px;
}

.author-stats {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
}

.author-stats div {
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  opacity: 0.5;
}

.author-stats span {
  font-size: 1.2rem;
  font-weight: bold;
  color: #fff;
  opacity: 1;
  margin-bottom: 5px;
}

.follow-btn {
  width: 100%;
  padding: 12px;
  background: #fff;
  color: #000;
  border: none;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.3s;
}

.follow-btn:hover {
  background: #6d5dfc;
  color: #fff;
}

.toc-card {
  top: 480px;
}

.toc-card h4 {
  margin-bottom: 20px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 2px;
}

.toc-card ul {
  list-style: none;
  padding: 0;
}

.toc-card li {
  margin-bottom: 15px;
}

.toc-card a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.95rem;
  transition: 0.3s;
  display: block;
}

.toc-card a:hover {
  color: #6d5dfc;
  transform: translateX(5px);
}

/* 底部相关阅读 */
.related-section {
  background: #0a0a0a;
  padding: 100px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.related-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
}

.related-section h3 {
  font-size: 1.5rem;
  margin-bottom: 40px;
  font-weight: 300;
  letter-spacing: 2px;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.related-card {
  background: #111;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: 0.4s;
  border: 1px solid transparent;
}

.related-card:hover {
  transform: translateY(-10px);
  border-color: rgba(109, 93, 252, 0.3);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
}

.r-cover {
  height: 200px;
  background-size: cover;
  background-position: center;
  transition: 0.4s;
}

.related-card:hover .r-cover {
  transform: scale(1.05);
}

.r-info {
  padding: 25px;
}

.r-category {
  font-size: 0.75rem;
  color: #6d5dfc;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: block;
  margin-bottom: 10px;
}

.r-info h4 {
  font-size: 1.2rem;
  line-height: 1.5;
  color: #fff;
}

.simple-footer {
  text-align: center;
  padding: 40px;
  opacity: 0.3;
  font-size: 0.8rem;
  background: #050505;
}

/* 响应式 */
@media (max-width: 1024px) {
  .article-container {
    grid-template-columns: 1fr;
  }

  .article-sidebar {
    display: none;
  }

  .article-hero h1 {
    font-size: 3rem;
  }

  .related-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .navbar {
    padding: 1rem 2rem;
  }

  .nav-links {
    display: none;
  }

  .article-hero h1 {
    font-size: 2.2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }

  .comment-input-area {
    flex-direction: column;
    align-items: stretch;
  }

  .submit-btn {
    align-self: flex-start;
    margin-top: 10px;
  }
}
</style>